/**
 * Pure helpers for the 7-day rain forecast (SPEC v0.9.3 Part A). NO imports, so
 * this file vendors into the API (scripts/vendor-sim.mjs) and imports into the web
 * app (@flowsight/sim) — one tested source of truth for rain-level classification,
 * multi-model agreement/confidence, and the Thai-calendar day labels.
 */
/** กรมอุตุนิยมวิทยา (TMD) 24-h rain criteria, mm (source: tmd.go.th). */
export const RAIN_HEAVY_MM = 35.1;
export const RAIN_VERYHEAVY_MM = 90.1;
/** Daily (24-h total) level per TMD: light 0.1–10 · moderate 10.1–35 · heavy 35.1–90 · very heavy ≥90.1. */
export function rainLevelOf(mm) {
    return mm < 0.1 ? "none" : mm <= 10 ? "light" : mm <= 35 ? "moderate" : mm <= 90 ? "heavy" : "veryheavy";
}
/**
 * Hourly rain-RATE bands (mm/h) for the per-hour sparkline — deliberately DISTINCT
 * from the 24-h TMD daily criteria (a whole day of "heavy" rarely falls in one hour).
 * "veryheavy" (red) is reserved for genuinely intense hours (≥30 mm/h).
 */
export const HOURLY_LIGHT_MMH = 2;
export const HOURLY_MODERATE_MMH = 10;
export const HOURLY_HEAVY_MMH = 30;
export function hourlyLevelOf(mmPerHour) {
    return mmPerHour < 0.1 ? "none"
        : mmPerHour < HOURLY_LIGHT_MMH ? "light"
            : mmPerHour < HOURLY_MODERATE_MMH ? "moderate"
                : mmPerHour < HOURLY_HEAVY_MMH ? "heavy"
                    : "veryheavy";
}
/**
 * Multi-model agreement for one day. `values` = each present model's 24-h total (mm);
 * missing models must already be filtered out so the denominator M reflects reality
 * (fix 2 — never hardcode "of 5"). Confidence measures how many models agree on the
 * ensemble-median's rain LEVEL, not how many see heavy rain (fix 5).
 */
export function dayAgreement(values, medianMm) {
    const m = values.length;
    const heavyCount = values.filter((v) => v >= RAIN_HEAVY_MM).length;
    const veryHeavyCount = values.filter((v) => v >= RAIN_VERYHEAVY_MM).length;
    const medianLevel = rainLevelOf(medianMm);
    const agreeCount = values.filter((v) => rainLevelOf(v) === medianLevel).length;
    const heavyFrac = m ? heavyCount / m : 0;
    const veryHeavyFrac = m ? veryHeavyCount / m : 0;
    const agreeFrac = m ? agreeCount / m : 0;
    const confidence = agreeFrac >= 0.8 ? "solid" : agreeFrac >= 0.6 ? "normal" : "faint";
    return {
        m, heavyCount, veryHeavyCount, medianLevel, agreeCount,
        heavyFrac, veryHeavyFrac, agreeFrac,
        isHeavyDay: m > 0 && heavyFrac >= 0.6,
        isVeryHeavyDay: m > 0 && veryHeavyFrac >= 0.6,
        confidence,
    };
}
/* ── Thai calendar (UTC+7, no DST) — day labels anchored to the Thai date (fix 3) ── */
/** The Thai-calendar date (YYYY-MM-DD) of an instant, correct in any server/browser tz. */
export function thaiDateStr(now) {
    return new Date(now.getTime() + 7 * 3600 * 1000).toISOString().slice(0, 10);
}
export function thaiToday(now) { return thaiDateStr(now); }
export function thaiTomorrow(now) { return thaiDateStr(new Date(now.getTime() + 24 * 3600 * 1000)); }
/** The Thai-calendar hour (0–23) of an instant. */
export function thaiHour(now) { return new Date(now.getTime() + 7 * 3600 * 1000).getUTCHours(); }
/** Part-of-day label for a Thai hour: กลางคืน 0–5 · เช้า 6–11 · บ่าย 12–17 · ค่ำ 18–23. */
export function thaiDayPart(hour) {
    const h = ((hour % 24) + 24) % 24;
    return h < 6 ? "กลางคืน" : h < 12 ? "เช้า" : h < 18 ? "บ่าย" : "ค่ำ";
}
/** Heaviest contiguous window of a 24-value hourly series (same rule as the time phrase). */
export function peakWindow(hrs) {
    if (!hrs.length)
        return null;
    const peak = Math.max(...hrs);
    if (peak < 0.5)
        return null;
    const thr = Math.max(1, peak * 0.35);
    const a = hrs.findIndex((v) => v >= thr);
    if (a < 0)
        return null;
    let b = a;
    for (let i = hrs.length - 1; i >= 0; i--)
        if (hrs[i] >= thr) {
            b = i;
            break;
        }
    return { start: a, end: Math.min(24, b + 1) };
}
/** Where the current hour sits relative to a window (for the "now-aware" today card). */
export function nowVsPeak(nowHour, w) {
    if (nowHour < w.start)
        return "before";
    if (nowHour >= w.end)
        return "after";
    return "during";
}
/** Best day in the next 3 where ≥80% of present models (and M ≥4) see VERY HEAVY rain. */
function veryHeavyWithin3(days) {
    const idxs = [];
    let n = 0, m = 0;
    for (let i = 0; i < Math.min(3, days.length); i++) {
        const d = days[i];
        if (d.m >= 4 && d.veryHeavyCount / d.m >= 0.8) { // D1(a) + D3 (M ≥4)
            idxs.push(i);
            if (d.veryHeavyCount > n) {
                n = d.veryHeavyCount;
                m = d.m;
            }
        }
    }
    return { qualifies: idxs.length > 0, n, m, idxs };
}
/** D1: show advice only when (a) very-heavy soon at the point OR the ท้ายเขื่อน zone,
 *  AND (b) the user is below a dam / in a GISTDA flood / near an over-bank station. */
export function decideAdvice(s) {
    const pt = veryHeavyWithin3(s.pointDays);
    const dam = s.damDownDays ? veryHeavyWithin3(s.damDownDays) : { qualifies: false, n: 0, m: 0, idxs: [] };
    const aOK = pt.qualifies || dam.qualifies;
    const bOK = s.isBelowDam || s.gistdaFloodInRadius || s.thaiWaterOverBankInRadius;
    const use = pt.qualifies ? pt : dam;
    return {
        show: aOK && bOK,
        via: !aOK ? null : pt.qualifies ? "point" : "dam",
        nModels: use.n, mModels: use.m, days: use.idxs,
        condB: s.isBelowDam ? "below" : s.gistdaFloodInRadius ? "flood" : s.thaiWaterOverBankInRadius ? "thaiwater" : null,
        ddpm: s.ddpmAnnounced,
    };
}
/* ── v0.9.6: per-day ensemble stats, shared by /api/rain (normalize) and the national province map ── */
export const RAIN_MODEL_IDS = ["ecmwf_ifs025", "gfs_seamless", "icon_seamless", "jma_seamless", "ukmo_global_deterministic_10km"];
const DOW_TH = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const med = (a) => { if (!a.length)
    return 0; const s = [...a].sort((x, y) => x - y), m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
const r1 = (n) => Math.round(n * 10) / 10;
/**
 * Daily 24-h totals of each model → per-day median / min–max / TMD level / agreement, for ≤7 days.
 * `daily` is Open-Meteo's `daily` object (time + precipitation_sum_<model>). A model/day with no
 * value — or a physically impossible negative one — is left out of that day (never guessed / zeroed);
 * a day no model covers has m = 0.
 */
export function dailyRainStats(daily, modelIds = RAIN_MODEL_IDS, maxDays = 7) {
    const dates = daily.time ?? [];
    const out = [];
    for (let d = 0; d < Math.min(maxDays, dates.length); d++) {
        const perModel = {};
        const vals = [];
        for (const id of modelIds) {
            const raw = daily[`precipitation_sum_${id}`]?.[d];
            // rain can't be negative: a model that reports < 0 (seen from JMA, e.g. -0.1) has NO valid value
            // that day — left out, never turned into 0
            const v = raw != null && raw >= 0 ? raw : null;
            perModel[id] = v == null ? null : r1(v);
            if (v != null)
                vals.push(v);
        }
        const median = r1(med(vals));
        const a = dayAgreement(vals, median);
        out.push({
            date: dates[d], dow: DOW_TH[new Date(dates[d] + "T00:00:00Z").getUTCDay()], perModel,
            m: a.m, min: vals.length ? r1(Math.min(...vals)) : 0, max: vals.length ? r1(Math.max(...vals)) : 0, median, level: a.medianLevel,
            heavyCount: a.heavyCount, veryHeavyCount: a.veryHeavyCount, isHeavyDay: a.isHeavyDay, isVeryHeavyDay: a.isVeryHeavyDay,
            chipLabel: a.isVeryHeavyDay ? "ฝนหนักมาก" : a.isHeavyDay ? "ฝนหนัก" : "", confidence: a.confidence,
        });
    }
    return out;
}
//# sourceMappingURL=rain-util.js.map